import { FormEvent, useEffect, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { api, ApiError, PaymentIntentResponse, SubscriptionPackage, TenantWorkspace } from "../lib/api.js";

function isSafeUrl(value: string): boolean {
  try {
    const u = new URL(value, window.location.href);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch { return false; }
}

function methodLabel(method: string): string {
  switch (method) {
    case "mpesa":         return "M-Pesa (Safaricom)";
    case "airtel-money":  return "Airtel Money";
    case "mtn-momo":      return "MTN MoMo";
    case "paypal":        return "PayPal";
    case "google-pay":    return "Google Pay";
    case "pesapal":       return "PesaPal";
    case "mobile-money":  return "Mobile Money (generic)";
    default:              return method.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

export default function Billing() {
  const [plans, setPlans] = useState<SubscriptionPackage[]>([]);
  const [workspaces, setWorkspaces] = useState<TenantWorkspace[]>([]);
  const [methods, setMethods] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [planId, setPlanId] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [method, setMethod] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<PaymentIntentResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.subscriptionPackages(),
      api.workspace(),
      api.paymentMethods()
    ])
      .then(([p, w, m]) => {
        if (cancelled) return;
        setPlans(p.packages ?? []);
        setWorkspaces(w.workspaces ?? []);
        setMethods(m.methods ?? []);
        if (w.workspaces && w.workspaces.length === 1) setWorkspaceId(w.workspaces[0].id);
      })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof ApiError ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const onPay = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setResult(null);
    try {
      const response = await api.initiatePayment({ planId, workspaceId, paymentMethod: method });
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Payment initiation failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Billing</h1>
          <p>Initiate a payment for a workspace against a configured plan and provider.</p>
        </div>
      </header>

      {error && <div className="alert alert-error">{error}</div>}

      <section className="section" style={{ maxWidth: 720 }}>
        <div className="section-head"><h2>Initiate payment</h2></div>
        <form className="card" onSubmit={onPay}>
          <div className="card-body">
            <div className="form-grid">
              <div className="field">
                <label htmlFor="plan">Plan</label>
                <select id="plan" required value={planId} onChange={(e) => setPlanId(e.target.value)} disabled={loading || plans.length === 0}>
                  <option value="">{plans.length ? "Select a plan…" : "No plans configured"}</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} — {p.waived || p.price === 0 ? "Free" : p.price.toFixed(2)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="ws">Workspace</label>
                {workspaces.length === 0 ? (
                  <input id="ws" type="text" required value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)} placeholder="workspace id" />
                ) : (
                  <select id="ws" required value={workspaceId} onChange={(e) => setWorkspaceId(e.target.value)}>
                    <option value="">Select a workspace…</option>
                    {workspaces.map((w) => (
                      <option key={w.id} value={w.id}>{w.name ?? w.id} ({w.id})</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="field">
                <label htmlFor="pm">Payment method</label>
                <select id="pm" required value={method} onChange={(e) => setMethod(e.target.value)} disabled={loading || methods.length === 0}>
                  <option value="">{methods.length ? "Select a method…" : "No providers configured"}</option>
                  {methods.map((m) => (
                    <option key={m} value={m}>{methodLabel(m)}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="card-head" style={{ borderTop: "1px solid var(--divider)", borderBottom: 0, justifyContent: "flex-end" }}>
            <button type="submit" className="btn btn-primary" disabled={submitting || !planId || !workspaceId || !method}>
              {submitting ? "Submitting…" : "Initiate payment"}
            </button>
          </div>
        </form>

        {result && (
          <div className="alert alert-info" style={{ marginTop: 16 }}>
            <div><strong>Status:</strong> {result.status}</div>
            {result.providerReference && (
              <div><strong>Reference:</strong> <code>{result.providerReference}</code></div>
            )}
            {result.redirectUrl && isSafeUrl(result.redirectUrl) && (
              <div style={{ marginTop: 6 }}>
                <a href={result.redirectUrl} target="_blank" rel="noopener noreferrer">Continue to provider ↗</a>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
